import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  OnDestroy,
  ViewChild
} from '@angular/core';
import {CameraType} from "../../service/camera.service";
import {RenderingService, RenderingStrategy} from "../../service/rendering.service";
import {ViewPortManager, ViewPortManagerStore} from "../../store/view-port-manager.store";
import {RendererStore} from "../../store/Renderer.store";
import {Object3DFactoryService} from "../../service/object3D.factory.service"
import {EnvironmentService} from "../../service/environment.service";
import {LamellaAreaObject} from "../../objects-3D/lamellaAreaObject";
import {LamelareaImpl} from "../../../shared/product-card/product-card/lamelarea.class";
import {TranslatePipe} from "@ngx-translate/core";
import {LamelareaStore, LamellaAreaStore} from "../../../shared/product-card/product-card/lamelarea.store";
import {SceneObjectManagerService} from "../../service/scene-object.manager.service";


@Component({
  selector: 'app-view-port',
  standalone: true,
  imports: [
    TranslatePipe
  ],
  templateUrl: './view-port.component.html',
  styleUrl: './view-port.component.scss'
})
export class ViewPortComponent implements OnDestroy, AfterViewInit{
  @ViewChild('threejsContainer', { static: true }) threejsContainer!: ElementRef;

  private readonly viewPortManager: ViewPortManager = inject(ViewPortManagerStore);
  private readonly renderingService = inject(RenderingService);
  private readonly rendererStore = inject(RendererStore);
  private readonly object3DFactoryService = inject(Object3DFactoryService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly sceneObjectManagerService = inject(SceneObjectManagerService);


  public error: boolean = false;

  @ViewChild('fullscreenContainer', { static: true }) fullscreenContainer!: ElementRef;


  readonly lamellaArea = input.required<LamelareaImpl>();
  readonly lamellaAreaStore: LamellaAreaStore = inject(LamelareaStore);


  public cameraType: InputSignal<CameraType> = input('perspective', { transform: (type: CameraType) => type } );
  public renderingStrategy: InputSignal<RenderingStrategy> = input<RenderingStrategy>(RenderingStrategy.cycled);

  private lamellaAreaObject: LamellaAreaObject;

  public loading: boolean = true; // Lataustila, aluksi true

  private intersectionObserver?: IntersectionObserver;
  private readonly isDesktop = window.matchMedia('(pointer: fine)').matches;

  public async start(){
    this.initializeViewPort().then(
      () => {
        //Remove class hidden
        this.threejsContainer.nativeElement.classList.remove('hidden');
      },
      (error) => console.error('Virhe näkymäportin alustuksessa:', error)
  )}

  private async initializeViewPort() {
    try {
      // Lisää uusi näkymäportti manageriin käyttäen ViewChild-referenssiä
      await this.viewPortManager.add(this.lamellaArea().id, this.threejsContainer.nativeElement, this.cameraType(), this.renderingStrategy());

      const viewPort = this.viewPortManager.getById(this.lamellaArea().id);
      const scene = viewPort.scene;

      // Alusta ympäristö
      await this.environmentService.init(scene, this.rendererStore.renderer);

      // Luo lamellaAreaObject ja lisää se sceneen
      this.object3DFactoryService.createLamellaAreaObject(this.lamellaArea()).then(
            (lamellaAreaObject) => {
                this.lamellaAreaObject = lamellaAreaObject;
                this.lamellaAreaObject.insert(scene);

                //updateView after lamellaAreaObject is created
                this.returnToAnchor();

                let superSamplingFactor = 1.5;
                if(!this.isDesktop) {
                  superSamplingFactor = 0.3;
                }

                // For now we need this to get viewPort right in mobile. FIX THIS!
                this.rendererStore.setSuperSamplingFactor(superSamplingFactor);

                this.loading = false;
                this.renderingService.startAnimate(true);

                //update hook that invokes render cycles
                lamellaAreaObject.change.subscribe(()=>this.renderingService.animateOnce());
            },
            (error) => console.error('Virhe lamellaAreaObjectin luonnissa:', error)
      )

    } catch (error) {
      console.error('Alustusten latausvirhe:', error);
      // Voit asettaa lataustilan false tai hallita virhetilaa erikseen
      this.loading = false;
      this.error = true;
    }
  }

  @HostListener('mousemove', ['$event'])
  @HostListener('touchstart', ['$event'])
  @HostListener('touchmove', ['$event'])
  @HostListener('wheel', ['$event'])
  private onEvent(event: Event) { this.cycledRender() }

  private cycledRender() {
    const cycled = this.rendererStore.renderingStrategy === 'cycled';
    if(cycled) { this.renderingService.startAnimate(cycled) }
  }

  public returnToAnchor() {
    this.sceneObjectManagerService.resetCamera(this.lamellaArea().id)
  }

  public toggleFullScreen() {
    if(this.isInFullscreen()){
        this.exitFullscreen();
    }
    else {
        this.enterFullscreen();
    }
  }

  ngAfterViewInit() {
    // Määritellään IntersectionObserver havaitsemaan näkyvyys
    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Elementti on näkyvissä
          this.onBecameVisible();
        } else {
          // Elementti ei ole näkyvissä
          this.onBecameHidden();
        }
      });
    }, {
      root: null,       // selataan sivua, null = selainikkuna
      threshold: 0.05    // laukaisee, kun 25% elementistä näkyy
    });

    // Tarkkaillaan nimenomaan threejsContainer-elementtiä
    this.intersectionObserver.observe(this.threejsContainer.nativeElement);
  }

  private onBecameVisible(): void {
    this.viewPortManager.setVisibility(this.lamellaArea().id, true);
    this.rendererStore.resetAnimationCycle(Date.now())
  }

  private onBecameHidden(): void {
    this.viewPortManager.setVisibility(this.lamellaArea().id, false);
  }

  ngOnDestroy() {
    // Poista IntersectionObserverin kuuntelija
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // remove view port from manager
    this.viewPortManager.remove(this.lamellaArea().id);
    this.sceneObjectManagerService.removeObject(this.lamellaArea().id)
  }

  toggleTrueSize() {
    this.lamellaAreaStore.updateLamelarea(this.lamellaArea().id, {'demoSize': !this.lamellaArea().demoSize});
  }

  setRenderQuality(quality: string) {
    const qualityFactors = {
      LOW: this.isDesktop ? 0.8 : 0.3,
      HIGH: this.isDesktop ? 1.5 : 0.5,
      SUPER: this.isDesktop ? 1.8 : 0.5,
    };

    const factor = qualityFactors[quality as keyof typeof qualityFactors];
    if (factor !== undefined) {
      this.rendererStore.setSuperSamplingFactor(factor);
    }
  }

  private originalParent!: Node;
  private nextSibling!: Node | null;

  enterFullscreen(): void {

    const elem = this.fullscreenContainer.nativeElement;

    const canvasParent = document.getElementById('canvas').parentElement;

    // Tallenna alkuperäinen parent ja seuraava sisarus
    this.originalParent = canvasParent.parentNode;

    this.nextSibling = canvasParent.nextSibling;

    elem.appendChild(canvasParent);

    this.viewPortManager.hideAllButThis(this.lamellaArea().id);

    const bodyWidth = document.body.clientWidth;
    const bodyHeight = document.body.clientHeight;

    this.rendererStore.resetAnimationCycle(Date.now());

    if (elem.requestFullscreen) {
      elem.requestFullscreen().then(()=>{
        this.returnToAnchor();
        this.setRenderQuality("SUPER");
        this.rendererStore.resizeCanvas(bodyWidth, bodyHeight);
      });
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen(); // Safari
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen(); // IE11
    }
  }

  exitFullscreen(): void {

    if (document.exitFullscreen) {

      document.exitFullscreen().then(() => {
        this.restoreCanvas()
        this.viewPortManager.restorePreviousVisibleState();
        this.returnToAnchor();
        this.rendererStore.resetAnimationCycle(Date.now());
        this.setRenderQuality("HIGH");
      });

    } else {

      // prefiksatut versiot…
      (document as any).webkitExitFullscreen?.();
      this.restoreCanvas();
      this.viewPortManager.restorePreviousVisibleState();
      this.rendererStore.resetAnimationCycle(Date.now());

    }

  }

  isInFullscreen(): boolean {
    return !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).msFullscreenElement
    );
  }

  private restoreCanvas() {
    const canvasParent = document.getElementById('canvas')!.parentElement!;
    // Palautus alkuperäiseen paikkaan
    if (this.nextSibling) {
      this.originalParent.insertBefore(canvasParent, this.nextSibling);
    } else {
      this.originalParent.appendChild(canvasParent);
    }
  }

}
